import React from 'react';
import { useNavigate } from 'react-router-dom';

const ContentSourcesPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-qr-dark via-gray-900 to-qr-dark text-white">
      <header className="p-6 border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/Qroke Logo.png" alt="QrOke Logo" className="w-10 h-10 rounded-lg" />
            <span className="text-white font-display font-bold text-2xl">QrOke</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/')} className="px-3 py-2 rounded-lg border border-white/20 text-gray-200">Home</button>
            <button onClick={() => navigate('/instructions')} className="px-3 py-2 rounded-lg bg-qr-primary hover:bg-qr-secondary text-white">Instructions</button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 md:p-10 space-y-8">
        <h1 className="text-3xl md:text-4xl font-display font-bold">Content Sources & Licensing</h1>

        <section className="bg-white/5 rounded-2xl p-6 md:p-8 border border-white/10 space-y-4">
          <h2 className="text-2xl font-bold">Streaming (YouTube) — Personal Use Only</h2>
          <p className="text-gray-200 leading-relaxed">
            The YouTube integration in QrOke is provided as a convenience for private, at‑home parties only.
            Use of streamed content in commercial venues may violate the streaming provider's terms of service and copyright law.
          </p>
          <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-4 text-yellow-100 text-sm">
            <strong>Important:</strong> QrOke does not cache, download, or store YouTube videos. It functions solely as a pass‑through player.
            Storing or redistributing streamed content is prohibited.
          </div>
        </section>

        <section className="bg-white/5 rounded-2xl p-6 md:p-8 border border-white/10 space-y-4">
          <h2 className="text-2xl font-bold">Local Song Repository (Venue Safe Harbor)</h2>
          <p className="text-gray-200 leading-relaxed">
            Venues and professional hosts can connect a local library of legally acquired karaoke tracks (e.g., MP3/OGG with graphics).
            QrOke acts as a media player. You are responsible for owning the appropriate licenses for public performance.
          </p>
          <ul className="list-disc pl-5 text-gray-300 space-y-1">
            <li>Keep your purchased karaoke files on your own device or local server.</li>
            <li>Connect the folder in Host Controls to browse and play locally.</li>
            <li>No uploading to QrOke; nothing leaves your device.</li>
          </ul>
        </section>

        <section className="bg-white/5 rounded-2xl p-6 md:p-8 border border-white/10 space-y-4">
          <h2 className="text-2xl font-bold">Licensed Karaoke Libraries (Recommended)</h2>
          <p className="text-gray-200 leading-relaxed">
            For commercial use, consider providers offering catalogs with public performance rights.
            Some vendors also provide professional licenses and APIs for integration.
          </p>
          <ul className="list-disc pl-5 text-gray-300 space-y-1">
            <li>
              Explore professional licensing options (e.g., vendors that offer a "Professional License").
            </li>
            <li>
              Public catalogs/search tools exist (e.g., OpenKJ Database). Use them to discover tracks and then purchase via a licensed vendor.
              <div className="text-xs text-gray-400">Note: QrOke does not scrape or cache external catalogs; we link out only.</div>
            </li>
            <li>
              Free karaoke tracks exist online from certain publishers; verify licensing terms before public performance.
            </li>
          </ul>
          <div className="text-sm text-gray-300 space-y-1">
            <div>
              • OpenKJ Database: <a className="text-qr-accent hover:underline" href="https://db.openkj.org/" target="_blank" rel="noopener">db.openkj.org</a>
            </div>
            <div>
              • Example free song list: <a className="text-qr-accent hover:underline" href="https://www.karaoke-version.com/karaoke_free/karaoke.html" target="_blank" rel="noopener">karaoke-version.com/karaoke_free</a>
            </div>
          </div>
        </section>

        <section className="bg-white/5 rounded-2xl p-6 md:p-8 border border-white/10 space-y-3">
          <h2 className="text-2xl font-bold">Affiliates & Partnerships</h2>
          <p className="text-gray-200 leading-relaxed">
            We may partner with licensed karaoke services or offer affiliate integrations. In such cases, the host/venue holds the license or subscription with the provider, and QrOke delivers the seamless experience.
          </p>
        </section>

        <div className="text-sm text-gray-400">
          This page is informational and not legal advice. For compliance questions, consult an attorney.
        </div>
      </main>

      <footer className="py-12 px-6 border-t border-white/10 mt-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-center text-gray-300 text-sm mb-2">
            <a href="https://qroke.org/terms-of-service" target="_blank" rel="noopener" className="hover:text-white">Terms of Service</a>
            <span className="hidden sm:inline">•</span>
            <a href="https://qroke.org/privacy-policy-for-qroke" target="_blank" rel="noopener" className="hover:text-white">Privacy Policy</a>
          </div>
          <div className="text-gray-400">© 2025 QrOke — Content Sources & Licensing</div>
        </div>
      </footer>
    </div>
  );
};

export default ContentSourcesPage;
